import React, { useState, useMemo } from "react";
import { Stack } from "@fluentui/react";
import { Box, Paper, Typography, Skeleton, LinearProgress, Chip } from "@mui/material";
import { People24Regular, Globe24Regular, ChevronRight20Regular, Info16Regular, Flag24Regular, History24Regular, BranchFork24Regular } from "@fluentui/react-icons";
import { THEME, useOverviewData, getTimeRangeLabel } from "./OverviewContext";
import FluentDonutChart from "../../../ChartComponents/FluentDountChart";
import { CUSTOMER_VIEW_STYLES } from "../../CustomerView/CustomerView.styles";
import CustomCard from "../../../SharedComponents/CustomCard";
import { CTLExploreDataButton } from "../utils/CTLExploreDataButton";
import {
    generatePipelineSnapshotQuery,
    generateInternalStatusQuery,
    generateSourceBreakdownQuery,
    generateGeographyBreakdownQuery,
    generatePriorityBreakdownQuery
} from "../utils/ctlKustoQueries";
import { CTLInfoTooltip } from "../utils/CTLInfoTooltip";

// Color palette for geography regions - single consistent color
const GEOGRAPHY_COLOR = "#0078D4"; // Primary blue for all regions

// Priority color configuration - primary colors
const PRIORITY_COLORS: Record<string, string> = {
    P0: "#0078D4",
    P1: "#107C10",
    P2: "#8764B8",
    P3: "#E74C3C"
};

// Pipeline Snapshot Component
const PipelineSnapshot: React.FC = () => {
    const { data, isLoading, dateRange, customDateRange } = useOverviewData();
    const [isExpanded, setIsExpanded] = useState(false);

    const timeRangeLabel = getTimeRangeLabel(dateRange, customDateRange);

    const getStatusColor = (status: string): string => {
        const lowerStatus = status.toLowerCase();
        if (lowerStatus.includes("under review")) return THEME.error;
        if (lowerStatus.includes("ga") || lowerStatus.includes("general")) return THEME.primary;
        if (lowerStatus.includes("rejected")) return "#6B7280";
        if (lowerStatus.includes("approved")) return THEME.primaryLight;
        if (lowerStatus.includes("pending") || lowerStatus.includes("submitted")) return "#9CA3AF";
        if (lowerStatus.includes("progress")) return "#F59E0B";
        return THEME.primary;
    };

    const totalRecords = data?.summary?.totalCustomerRequests ?? 0;

    const { allData, displayData, othersCount, totalInFlight } = useMemo(() => {
        const all = data?.pipelineSnapshot && data.pipelineSnapshot.length > 0
            ? data.pipelineSnapshot
            : [];

        const total = all.reduce((sum, s) => sum + s.count, 0);
        const top5 = all.slice(0, 5);
        const othersItems = all.slice(5);

        return { allData: all, displayData: top5, othersCount: othersItems.length, totalInFlight: total > 0 ? total : totalRecords };
    }, [data, totalRecords]);

    const itemsToShow = isExpanded ? allData : displayData;

    if (isLoading) {
        return <Skeleton variant="rectangular" height={280} sx={{ borderRadius: "12px" }} />;
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                borderRadius: "12px",
                border: `1px solid ${THEME.border}`,
                backgroundColor: THEME.surface,
                height: 280,
                display: "flex",
                flexDirection: "column"
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: "15px", fontWeight: 700, color: THEME.textPrimary }}>
                            Customer Requests by Pipeline Snapshot
                        </Typography>
                        <CTLInfoTooltip
                            title="Customer Requests by Pipeline Snapshot"
                            description="What we would tell a customer if they asked 'where is my request today?'. It shows how the whole request pool is spread across the customer-facing stages, so you can see whether requests are piling up before a decision or moving through to delivery."
                            howItsCalculated="Every request is placed in exactly one bucket based on its current customer-facing status. The bar length is that bucket's share of all requests in your selected product and time period."
                            note="Only the five largest stages are shown by default — expand the card to see the rest."
                        />
                        <Chip
                            icon={<People24Regular style={{ fontSize: 12 }} />}
                            label="Customer-facing status"
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: "10px",
                                fontWeight: 700,
                                backgroundColor: "#E7F1FF",
                                color: THEME.primary,
                                "& .MuiChip-icon": { color: "inherit", marginLeft: "4px" }
                            }}
                        />
                        <Chip
                            icon={<History24Regular style={{ fontSize: 12 }} />}
                            label={timeRangeLabel}
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: "10px",
                                fontWeight: 600,
                                backgroundColor: dateRange === "2years" ? "#E7F1FF" : "#FFF4CE",
                                color: dateRange === "2years" ? THEME.primary : "#996800",
                                "& .MuiChip-icon": {
                                    color: "inherit",
                                    marginLeft: "4px"
                                }
                            }}
                        />
                    </Box>
                    <Typography sx={{ fontSize: "11px", color: THEME.textSecondary }}>
                        The status customers see for their requests
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: THEME.textPrimary }}>
                        {totalInFlight.toLocaleString()} <span style={{ fontWeight: 400, color: THEME.textSecondary }}>in flight</span>
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: "auto", pr: 0.5 }}>
                <Stack tokens={{ childrenGap: 10 }}>
                    {itemsToShow.map((item) => {
                        // Calculate actual percentage for display
                        const actualPct = totalInFlight > 0 ? (item.count / totalInFlight) * 100 : 0;
                        const displayPct = actualPct > 0 && actualPct < 1 ? "<1" : Math.round(actualPct).toString();

                        return (
                            <Box key={item.status}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                                    <Typography sx={{ fontSize: "13px", color: THEME.textPrimary }}>
                                        {item.status}
                                    </Typography>
                                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: THEME.textPrimary }}>
                                        {item.count.toLocaleString()} <span style={{ fontWeight: 400, color: THEME.textSecondary }}>({displayPct}%)</span>
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.max(actualPct, 0.5)}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: "#F3F4F6",
                                        "& .MuiLinearProgress-bar": {
                                            backgroundColor: getStatusColor(item.status),
                                            borderRadius: 4
                                        }
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Stack>
            </Box>

            {allData.length > 5 && (
                <Box
                    onClick={() => setIsExpanded(!isExpanded)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        pt: 1,
                        mt: 1,
                        borderTop: `1px solid ${THEME.border}`,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#F9FAFB" },
                        borderRadius: 1,
                        flexShrink: 0
                    }}
                >
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: THEME.primary }}>
                        {isExpanded ? "Show less" : `Show ${othersCount} more`}
                    </Typography>
                    <ChevronRight20Regular
                        style={{
                            color: THEME.primary,
                            transform: isExpanded ? "rotate(-90deg)" : "rotate(90deg)",
                            transition: "transform 0.2s"
                        }}
                    />
                </Box>
            )}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    minHeight: 45,
                    borderTop: `2px solid ${THEME.border}`,
                    mt: 1,
                    pt: 1,
                    flexShrink: 0
                }}
            >
                <CTLExploreDataButton
                    query={generatePipelineSnapshotQuery({ dateRange })}
                    chartTitle="Pipeline Snapshot"
                />
            </Box>
        </Paper>
    );
};

// Engineering / internal status color mapping for the Internal Status Snapshot
const getInternalStatusColor = (status: string): string => {
    const s = status.toLowerCase();
    if (s.includes("develop")) return "#0078D4";           // In Development - blue
    if (s.includes("preview")) return "#00B294";           // In Preview Release - teal
    if (s.includes("general") || s.includes("ga")) return "#107C10"; // Generally Available - green
    if (s.includes("backlog")) return "#8764B8";           // In Engineering Backlog - violet
    if (s.includes("triage")) return "#F59E0B";            // Pending Triage - amber
    if (s.includes("evidence")) return "#0EA5E9";          // Evidence Gathering - sky
    if (s.includes("more info") || s.includes("needs")) return "#D97706"; // Needs more info - orange
    if (s.includes("reject")) return "#6B7280";            // Rejected - gray
    return "#5A5A72";
};

// Internal Status Snapshot Component - engineering-facing status (same bar style as Pipeline Snapshot)
const InternalStatusSnapshot: React.FC = () => {
    const { data, isLoading, dateRange, customDateRange } = useOverviewData();
    const [isExpanded, setIsExpanded] = useState(false);

    const timeRangeLabel = getTimeRangeLabel(dateRange, customDateRange);

    const { allData, displayData, othersCount, totalInFlight } = useMemo(() => {
        const all = data?.internalStatusSnapshot && data.internalStatusSnapshot.length > 0
            ? data.internalStatusSnapshot
            : [];
        const total = all.reduce((sum, s) => sum + s.count, 0);
        const top5 = all.slice(0, 5);
        const othersItems = all.slice(5);
        return { allData: all, displayData: top5, othersCount: othersItems.length, totalInFlight: total };
    }, [data]);

    const itemsToShow = isExpanded ? allData : displayData;

    if (isLoading) {
        return <Skeleton variant="rectangular" height={280} sx={{ borderRadius: "12px" }} />;
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                borderRadius: "12px",
                border: `1px solid ${THEME.border}`,
                backgroundColor: THEME.surface,
                height: 280,
                display: "flex",
                flexDirection: "column"
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, flexShrink: 0, gap: 1 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <BranchFork24Regular style={{ color: "#8764B8" }} />
                        <Typography sx={{ fontSize: "15px", fontWeight: 700, color: THEME.textPrimary }}>
                            Requests by Internal (Engineering) Status
                        </Typography>
                        <CTLInfoTooltip
                            title="Requests by Internal (Engineering) Status"
                            description="The same set of requests seen through engineering's eyes. Comparing this with the customer-facing view alongside it shows whether what we tell customers matches what engineering is actually doing."
                            howItsCalculated="Every request is placed in exactly one bucket based on the status engineering holds against it (for example pending triage, in backlog, in development). The bar length is that bucket's share of all requests in your selected product and time period."
                            note="Only the five largest statuses are shown by default — expand the card to see the rest."
                        />
                        <Chip
                            icon={<BranchFork24Regular style={{ fontSize: 12 }} />}
                            label="Engineering status"
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: "10px",
                                fontWeight: 700,
                                backgroundColor: "#F3EEFB",
                                color: "#8764B8",
                                "& .MuiChip-icon": { color: "inherit", marginLeft: "4px" }
                            }}
                        />
                        <Chip
                            icon={<History24Regular style={{ fontSize: 12 }} />}
                            label={timeRangeLabel}
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: "10px",
                                fontWeight: 600,
                                backgroundColor: dateRange === "2years" ? "#E7F1FF" : "#FFF4CE",
                                color: dateRange === "2years" ? THEME.primary : "#996800",
                                "& .MuiChip-icon": { color: "inherit", marginLeft: "4px" }
                            }}
                        />
                    </Box>
                    <Typography sx={{ fontSize: "11px", color: THEME.textSecondary }}>
                        The internal status used by engineering teams
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: "12px", color: THEME.textSecondary, whiteSpace: "nowrap" }}>
                        {totalInFlight.toLocaleString()} total
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: "auto", pr: 0.5 }}>
                <Stack tokens={{ childrenGap: 10 }}>
                    {itemsToShow.map((item) => {
                        const actualPct = totalInFlight > 0 ? (item.count / totalInFlight) * 100 : 0;
                        const displayPct = actualPct > 0 && actualPct < 1 ? "<1" : Math.round(actualPct).toString();

                        return (
                            <Box key={item.status}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                                    <Typography sx={{ fontSize: "13px", color: THEME.textPrimary }}>
                                        {item.status}
                                    </Typography>
                                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: THEME.textPrimary }}>
                                        {item.count.toLocaleString()} <span style={{ fontWeight: 400, color: THEME.textSecondary }}>({displayPct}%)</span>
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.max(actualPct, 0.5)}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: "#F3F4F6",
                                        "& .MuiLinearProgress-bar": {
                                            backgroundColor: getInternalStatusColor(item.status),
                                            borderRadius: 4
                                        }
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Stack>
            </Box>

            {allData.length > 5 && (
                <Box
                    onClick={() => setIsExpanded(!isExpanded)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        pt: 1,
                        mt: 1,
                        borderTop: `1px solid ${THEME.border}`,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#F9FAFB" },
                        borderRadius: 1,
                        flexShrink: 0
                    }}
                >
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: THEME.primary }}>
                        {isExpanded ? "Show less" : `Show ${othersCount} more`}
                    </Typography>
                    <ChevronRight20Regular
                        style={{
                            color: THEME.primary,
                            transform: isExpanded ? "rotate(-90deg)" : "rotate(90deg)",
                            transition: "transform 0.2s"
                        }}
                    />
                </Box>
            )}

            {allData.length === 0 && (
                <Typography sx={{ fontSize: "13px", color: THEME.textSecondary, textAlign: "center", py: 3 }}>
                    No internal status data available.
                </Typography>
            )}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    minHeight: 45,
                    borderTop: `2px solid ${THEME.border}`,
                    mt: 1,
                    pt: 1,
                    flexShrink: 0
                }}
            >
                <CTLExploreDataButton
                    query={generateInternalStatusQuery({ dateRange })}
                    chartTitle="Internal Status Snapshot"
                />
            </Box>
        </Paper>
    );
};

// Customer Requests by Source Component
const CustomerRequestsBySource: React.FC = () => {
    const { data, isLoading, dateRange, customDateRange } = useOverviewData();
    const [isExpanded, setIsExpanded] = useState(false);

    const timeRangeLabel = getTimeRangeLabel(dateRange, customDateRange);

    const totalRecords = data?.summary?.totalCustomerRequests ?? 0;

    const { allData, displayData, othersCount, totalCount } = useMemo(() => {
        const all = data?.sourceBreakdown && data.sourceBreakdown.length > 0
            ? data.sourceBreakdown
            : [];

        const total = all.reduce((sum, s) => sum + s.count, 0);
        const top8 = all.slice(0, 8);
        const othersItems = all.slice(8);

        return { allData: all, displayData: top8, othersCount: othersItems.length, totalCount: total > 0 ? total : totalRecords };
    }, [data, totalRecords]);

    const itemsToShow = isExpanded ? allData : displayData;
    const maxCount = Math.max(...allData.map((s) => s.count), 1);

    if (isLoading) {
        return <Skeleton variant="rectangular" height={280} sx={{ borderRadius: "12px" }} />;
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                borderRadius: "12px",
                border: `1px solid ${THEME.border}`,
                backgroundColor: THEME.surface,
                height: 280,
                display: "flex",
                flexDirection: "column"
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexShrink: 0, flexWrap: "wrap" }}>
                <People24Regular style={{ color: THEME.primary }} />
                <Typography sx={{ fontSize: "15px", fontWeight: 700, color: THEME.textPrimary }}>
                    Customer Requests by Submitting Organization
                </Typography>
                <CTLInfoTooltip
                    title="Customer Requests by Submitting Organization"
                    description="Which teams are bringing customer feedback to us. It tells you where our listening channels are working well and which parts of the field are quiet and may need encouragement."
                    howItsCalculated="Each request is attributed to the organisation that submitted it, and we count how many came from each. The bar length compares each organisation against the largest contributor."
                    note="Requests with no recorded submitting organisation are left out. Top 8 shown by default."
                />
                <Chip
                    icon={<History24Regular style={{ fontSize: 12 }} />}
                    label={timeRangeLabel}
                    size="small"
                    sx={{
                        height: 20,
                        fontSize: "10px",
                        fontWeight: 600,
                        backgroundColor: dateRange === "2years" ? "#E7F1FF" : "#FFF4CE",
                        color: dateRange === "2years" ? THEME.primary : "#996800",
                        "& .MuiChip-icon": {
                            color: "inherit",
                            marginLeft: "4px"
                        }
                    }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                    <Typography sx={{ fontSize: "12px", color: THEME.textSecondary }}>
                        {totalCount.toLocaleString()} total
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: "auto", pr: 0.5 }}>
                <Stack tokens={{ childrenGap: 5 }}>
                    {itemsToShow.map((item) => (
                        <Box key={item.source} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    color: THEME.textPrimary,
                                    width: 85,
                                    flexShrink: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap"
                                }}
                                title={item.source}
                            >
                                {item.source}
                            </Typography>
                            <Box sx={{ flex: 1, height: 12, backgroundColor: "#F3F4F6", borderRadius: 3 }}>
                                <Box
                                    sx={{
                                        width: `${(item.count / maxCount) * 100}%`,
                                        height: "100%",
                                        backgroundColor: THEME.primary,
                                        borderRadius: 3,
                                        minWidth: 4
                                    }}
                                />
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: THEME.textPrimary,
                                    minWidth: 45,
                                    textAlign: "right"
                                }}
                            >
                                {item.count.toLocaleString()}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </Box>

            {allData.length > 8 && (
                <Box
                    onClick={() => setIsExpanded(!isExpanded)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        pt: 1,
                        mt: 1,
                        borderTop: `1px solid ${THEME.border}`,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#F9FAFB" },
                        borderRadius: 1,
                        flexShrink: 0
                    }}
                >
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: THEME.primary }}>
                        {isExpanded ? "Show less" : `Show ${othersCount} more`}
                    </Typography>
                    <ChevronRight20Regular
                        style={{
                            color: THEME.primary,
                            transform: isExpanded ? "rotate(-90deg)" : "rotate(90deg)",
                            transition: "transform 0.2s"
                        }}
                    />
                </Box>
            )}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    minHeight: 45,
                    borderTop: `2px solid ${THEME.border}`,
                    mt: 1,
                    pt: 1,
                    flexShrink: 0
                }}
            >
                <CTLExploreDataButton
                    query={generateSourceBreakdownQuery({ dateRange })}
                    chartTitle="Source Breakdown"
                />
            </Box>
        </Paper>
    );
};

// Customer Requests by Geography Component (Bar Chart) - for combined section
const GeographyBarChart: React.FC = () => {
    const { data, isLoading, dateRange, customDateRange } = useOverviewData();
    const [isExpanded, setIsExpanded] = useState(false);

    const timeRangeLabel = getTimeRangeLabel(dateRange, customDateRange);

    const totalRecords = data?.summary?.totalCustomerRequests ?? 0;

    const { allData, displayData, othersCount, totalCount } = useMemo(() => {
        const all = data?.geographyBreakdown && data.geographyBreakdown.length > 0
            ? data.geographyBreakdown
            : [];

        const total = all.reduce((sum, g) => sum + g.count, 0);
        const top8 = all.slice(0, 8);
        const othersItems = all.slice(8);

        return { allData: all, displayData: top8, othersCount: othersItems.length, totalCount: total > 0 ? total : totalRecords };
    }, [data, totalRecords]);

    const itemsToShow = isExpanded ? allData : displayData;
    const maxCount = Math.max(...allData.map((g) => g.count), 1);

    if (isLoading) {
        return <Skeleton variant="rectangular" height={280} sx={{ borderRadius: "12px" }} />;
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                borderRadius: "12px",
                border: `1px solid ${THEME.border}`,
                backgroundColor: THEME.surface,
                height: "100%",
                minHeight: 280,
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column"
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexShrink: 0, flexWrap: "wrap" }}>
                <Globe24Regular style={{ color: THEME.success }} />
                <Typography sx={{ fontSize: "15px", fontWeight: 700, color: THEME.textPrimary }}>
                    Customer Requests by A16 Region
                </Typography>
                <CTLInfoTooltip
                    title="Customer Requests by A16 Region"
                    description="Where in the world the demand is coming from. Useful for spotting whether a gap is global or specific to one market, and for planning regional engagement."
                    howItsCalculated="Each request is assigned to the customer's A16 sales region and we count how many fall into each. The bar length compares each region against the largest one."
                    note="Requests without a region on record are grouped as 'Not Available' and listed last. Top 8 shown by default."
                />
                <Chip
                    icon={<History24Regular style={{ fontSize: 12 }} />}
                    label={timeRangeLabel}
                    size="small"
                    sx={{
                        height: 20,
                        fontSize: "10px",
                        fontWeight: 600,
                        backgroundColor: dateRange === "2years" ? "#E7F1FF" : "#FFF4CE",
                        color: dateRange === "2years" ? THEME.primary : "#996800",
                        "& .MuiChip-icon": {
                            color: "inherit",
                            marginLeft: "4px"
                        }
                    }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                    <Typography sx={{ fontSize: "12px", color: THEME.textSecondary }}>
                        {totalCount.toLocaleString()} total
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: "auto", pr: 0.5 }}>
                <Stack tokens={{ childrenGap: 5 }}>
                    {itemsToShow.map((item, index) => (
                        <Box key={item.region} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    color: THEME.textPrimary,
                                    width: 85,
                                    flexShrink: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap"
                                }}
                                title={item.region}
                            >
                                {item.region}
                            </Typography>
                            <Box sx={{ flex: 1, height: 12, backgroundColor: "#F3F4F6", borderRadius: 3 }}>
                                <Box
                                    sx={{
                                        width: `${(item.count / maxCount) * 100}%`,
                                        height: "100%",
                                        backgroundColor: GEOGRAPHY_COLOR,
                                        borderRadius: 3,
                                        minWidth: 4
                                    }}
                                />
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: THEME.textPrimary,
                                    minWidth: 45,
                                    textAlign: "right"
                                }}
                            >
                                {item.count.toLocaleString()}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </Box>

            {allData.length > 8 && (
                <Box
                    onClick={() => setIsExpanded(!isExpanded)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        pt: 1,
                        mt: 1,
                        borderTop: `1px solid ${THEME.border}`,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#F9FAFB" },
                        borderRadius: 1,
                        flexShrink: 0
                    }}
                >
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: THEME.primary }}>
                        {isExpanded ? "Show less" : `Show ${othersCount} more`}
                    </Typography>
                    <ChevronRight20Regular
                        style={{
                            color: THEME.primary,
                            transform: isExpanded ? "rotate(-90deg)" : "rotate(90deg)",
                            transition: "transform 0.2s"
                        }}
                    />
                </Box>
            )}

            {allData.length === 0 && (
                <Typography sx={{ fontSize: "13px", color: THEME.textSecondary, textAlign: "center", py: 3 }}>
                    No geography data available.
                </Typography>
            )}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    minHeight: 45,
                    borderTop: `2px solid ${THEME.border}`,
                    mt: 1,
                    pt: 1,
                    flexShrink: 0
                }}
            >
                <CTLExploreDataButton
                    query={generateGeographyBreakdownQuery({ dateRange })}
                    chartTitle="Geography Breakdown"
                />
            </Box>
        </Paper>
    );
};

// Priority Donut Chart - for combined section
const PriorityDonutChart: React.FC = () => {
    const { data, isLoading, dateRange, customDateRange } = useOverviewData();

    const timeRangeLabel = getTimeRangeLabel(dateRange, customDateRange);

    const priorityData = data?.priorityBreakdown ?? [];
    const totalCount = priorityData.reduce((sum, p) => sum + p.count, 0);

    const chartData = useMemo(() => {
        return priorityData.map((item) => ({
            legend: item.priority,
            data: item.count,
            color: PRIORITY_COLORS[item.priority] || THEME.textSecondary
        }));
    }, [priorityData]);

    if (isLoading) {
        return (
            <div style={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Skeleton variant="circular" width={200} height={200} />
            </div>
        );
    }

    return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Chip
                    icon={<History24Regular style={{ fontSize: 12 }} />}
                    label={timeRangeLabel}
                    size="small"
                    sx={{
                        height: 20,
                        fontSize: "10px",
                        fontWeight: 600,
                        backgroundColor: dateRange === "2years" ? "#E7F1FF" : "#FFF4CE",
                        color: dateRange === "2years" ? THEME.primary : "#996800",
                        "& .MuiChip-icon": {
                            color: "inherit",
                            marginLeft: "4px"
                        }
                    }}
                />
            </Box>
            {chartData.length > 0 ? (
                <FluentDonutChart
                    data={{
                        chartTitle: "Priority Breakdown",
                        chartData: chartData
                    }}
                    height={230}
                    width={300}
                    valueInsideDonut={totalCount}
                    styles={CUSTOMER_VIEW_STYLES.COMMON_STYLES.CSS_STYLES.donutChart}
                />
            ) : (
                <Typography sx={{ fontSize: "13px", color: THEME.textSecondary }}>
                    No priority data available.
                </Typography>
            )}
        </Box>
    );
};

// Combined Charts Section - Geography Bar Chart and Priority Donut side by side
export const CombinedDonutCharts: React.FC = () => {
    const { isLoading, dateRange } = useOverviewData();

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
                    gap: 2,
                    alignItems: "stretch"
                }}
            >
                <Skeleton variant="rectangular" height={280} sx={{ borderRadius: "12px" }} />
                <Skeleton variant="rectangular" height={280} sx={{ borderRadius: "12px" }} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch"
            }}
        >
            {/* Geography Bar Chart */}
            <GeographyBarChart />

            {/* Priority Donut Chart - matching Paper structure for equal height */}
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    border: `1px solid ${THEME.border}`,
                    backgroundColor: THEME.surface,
                    height: "100%",
                    minHeight: 280,
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column"
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexShrink: 0, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: "15px", fontWeight: 700, color: THEME.textPrimary }}>
                        Customer Request Breakdown by Priority
                    </Typography>
                    <CTLInfoTooltip
                        title="Customer Request Breakdown by Priority"
                        description="How urgent the demand is overall. A large P0/P1 share signals pressure on engineering to act quickly, while a P2/P3-heavy mix suggests demand is mostly 'nice to have'."
                        howItsCalculated="Each request carries a priority set during triage. We count the requests in each priority tier and show each tier's share of the total as a slice of the ring."
                        note="Requests that have not yet been given a priority are excluded from this chart."
                    />
                </Box>
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <PriorityDonutChart />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        minHeight: 45,
                        borderTop: `2px solid ${THEME.border}`,
                        mt: 1,
                        pt: 1,
                        flexShrink: 0
                    }}
                >
                    <CTLExploreDataButton
                        query={generatePriorityBreakdownQuery({ dateRange })}
                        chartTitle="Priority Breakdown"
                    />
                </Box>
            </Paper>
        </Box>
    );
};

// Main BreakdownCharts Component (Pipeline, Internal Status and Source)
const BreakdownCharts: React.FC = () => {
    return (
        <Stack tokens={{ childrenGap: 16 }}>
            {/* Row 1: Two status views side by side — customer-facing vs engineering-internal */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
                    gap: 2,
                    alignItems: "start"
                }}
            >
                <PipelineSnapshot />
                <InternalStatusSnapshot />
            </Box>

            {/* Row 2: Source breakdown (full width) */}
            <CustomerRequestsBySource />
        </Stack>
    );
};

export default BreakdownCharts;
